import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ClaimFilingWizard from './components/ClaimFilingWizard';
import DisputeCard from './components/DisputeCard';
import DisputeDetailModal from './components/DisputeDetailModal';
import { supabase } from '../../lib/supabase';

const DisputeResolution = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [showFilingWizard, setShowFilingWizard] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Real-time subscription for violation charges (disputes)
  useEffect(() => {
    if (!user?.id) return;

    const disputesChannel = supabase?.
    channel('user_disputes')?.
    on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'rental_violation_charges',
        filter: `renter_id=eq.${user?.id}`
      },
      (payload) => {
        if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
          // Refresh disputes list when charge is added or updated
          window.location?.reload();
        }
      }
    )?.
    on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'rental_violation_charges',
        filter: `owner_id=eq.${user?.id}`
      },
      (payload) => {
        if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
          // Refresh disputes list for owner
          window.location?.reload();
        }
      }
    )?.
    subscribe();

    return () => {
      supabase?.removeChannel(disputesChannel);
    };
  }, [user?.id]);

  useEffect(() => {
    // Mock disputes data
    setDisputes([
    {
      id: 1,
      caseNumber: 'DSP-2026-0241',
      title: 'Trailer damage - rear bumper',
      disputeType: 'damage',
      status: 'submitted',
      priority: 'high',
      createdDate: '2026-02-13',
      lastUpdate: '2026-02-14',
      trailerName: 'Heavy Duty Utility Trailer',
      trailerImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_13ef7a567-1769876899260.png',
      trailerImageAlt: 'Heavy duty utility trailer',
      opposingParty: 'John Anderson',
      opposingPartyRole: 'owner',
      amount: 450,
      description: 'Rear bumper was damaged during rental period. Requesting repair cost coverage.',
      evidence: [
      { id: 1, type: 'image', url: "https://img.rocket.new/generatedImages/rocket_gen_img_161a58648-1771105496651.png", name: 'damage-photo-1.jpg', alt: 'Damaged trailer rear bumper' },
      { id: 2, type: 'image', url: "https://images.unsplash.com/photo-1720616192875-07312b103097", name: 'damage-photo-2.jpg', alt: 'Close-up of bumper damage' }],

      timeline: [
      { date: '2026-02-13 10:30', action: 'Claim submitted', actor: 'You', description: 'Initial claim filed with evidence' },
      { date: '2026-02-13 10:35', action: 'Notification sent', actor: 'System', description: 'Owner notified of claim' },
      { date: '2026-02-14 09:15', action: 'Under review', actor: 'System', description: 'Claim moved to review stage' }],

      responses: []
    },
    {
      id: 2,
      caseNumber: 'DSP-2026-0238',
      title: 'No-show rental',
      disputeType: 'no-show',
      status: 'awaiting_response',
      priority: 'medium',
      createdDate: '2026-02-10',
      lastUpdate: '2026-02-12',
      trailerName: 'Enclosed Cargo Trailer',
      trailerImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b2a7fed7-1767694944518.png',
      trailerImageAlt: 'Enclosed cargo trailer',
      opposingParty: 'Sarah Mitchell',
      opposingPartyRole: 'renter',
      amount: 180,
      description: 'Renter did not show up for scheduled pickup. Requesting compensation for lost rental income.',
      evidence: [
      { id: 1, type: 'document', url: '#', name: 'booking-confirmation.pdf', alt: 'Booking confirmation document' },
      { id: 2, type: 'document', url: '#', name: 'communication-log.pdf', alt: 'Communication log with renter' }],

      timeline: [
      { date: '2026-02-10 14:20', action: 'Claim submitted', actor: 'You', description: 'No-show claim filed' },
      { date: '2026-02-10 14:25', action: 'Notification sent', actor: 'System', description: 'Renter notified of claim' },
      { date: '2026-02-11 16:00', action: 'Response requested', actor: 'System', description: 'Awaiting renter response' }],

      responses: [
      { id: 1, sender: 'Sarah Mitchell', role: 'renter', message: 'I had a family emergency and tried to contact you but got no response.', timestamp: '2026-02-11 18:30', evidence: [] }]

    },
    {
      id: 3,
      caseNumber: 'DSP-2026-0235',
      title: 'Policy violation - unauthorized use',
      disputeType: 'policy_violation',
      status: 'mediation',
      priority: 'high',
      createdDate: '2026-02-08',
      lastUpdate: '2026-02-13',
      trailerName: 'Flatbed Equipment Trailer',
      trailerImage: "https://images.unsplash.com/photo-1604518207823-09b406f0f298",
      trailerImageAlt: 'Flatbed equipment trailer',
      opposingParty: 'Michael Brown',
      opposingPartyRole: 'renter',
      amount: 850,
      description: 'Trailer was used for commercial hauling which violates rental agreement terms.',
      evidence: [
      { id: 1, type: 'image', url: "https://img.rocket.new/generatedImages/rocket_gen_img_172178322-1771105498883.png", name: 'violation-photo.jpg', alt: 'Trailer being used for commercial hauling' },
      { id: 2, type: 'video', url: '#', name: 'violation-video.mp4', alt: 'Video evidence of unauthorized commercial use' }],

      timeline: [
      { date: '2026-02-08 11:00', action: 'Claim submitted', actor: 'You', description: 'Policy violation claim filed' },
      { date: '2026-02-08 11:05', action: 'Notification sent', actor: 'System', description: 'Renter notified of claim' },
      { date: '2026-02-09 15:30', action: 'Response received', actor: 'Michael Brown', description: 'Renter submitted counter-evidence' },
      { date: '2026-02-12 10:00', action: 'Mediation requested', actor: 'Michael Brown', description: 'Case escalated to mediation' },
      { date: '2026-02-13 09:00', action: 'Mediator assigned', actor: 'System', description: 'Admin mediator reviewing case' }],

      responses: [
      { id: 1, sender: 'Michael Brown', role: 'renter', message: 'I was only moving my personal equipment. This was not commercial use.', timestamp: '2026-02-09 15:30', evidence: [{ id: 1, type: 'image', url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac306b8a-1771105497753.png", name: 'personal-equipment.jpg', alt: 'Personal equipment being transported' }] }]

    },
    {
      id: 4,
      caseNumber: 'DSP-2026-0230',
      title: 'Excessive cleaning fee dispute',
      disputeType: 'damage',
      status: 'resolved',
      priority: 'low',
      createdDate: '2026-02-05',
      lastUpdate: '2026-02-11',
      trailerName: 'Utility Trailer 6x12',
      trailerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c80565ad-1765174544229.png",
      trailerImageAlt: 'Utility trailer',
      opposingParty: 'Emily Davis',
      opposingPartyRole: 'owner',
      amount: 50,
      description: 'Disputing excessive cleaning fee. Trailer was returned in clean condition.',
      evidence: [
      { id: 1, type: 'image', url: "https://img.rocket.new/generatedImages/rocket_gen_img_1638bc9f5-1771105499814.png", name: 'clean-trailer.jpg', alt: 'Clean trailer interior before return' }],

      timeline: [
      { date: '2026-02-05 16:00', action: 'Claim submitted', actor: 'You', description: 'Cleaning fee dispute filed' },
      { date: '2026-02-05 16:05', action: 'Notification sent', actor: 'System', description: 'Owner notified of claim' },
      { date: '2026-02-06 10:30', action: 'Response received', actor: 'Emily Davis', description: 'Owner provided counter-evidence' },
      { date: '2026-02-10 14:00', action: 'Admin review', actor: 'Admin', description: 'Case reviewed by platform admin' },
      { date: '2026-02-11 11:00', action: 'Resolved', actor: 'Admin', description: 'Partial refund issued - $25' }],

      responses: [
      { id: 1, sender: 'Emily Davis', role: 'owner', message: 'The trailer required professional cleaning due to mud and debris.', timestamp: '2026-02-06 10:30', evidence: [{ id: 1, type: 'image', url: 'https://images.unsplash.com/photo-1620121470267-2efe4d6c24fb', name: 'dirty-trailer.jpg', alt: 'Trailer with mud and debris' }] },
      { id: 2, sender: 'Platform Admin', role: 'admin', message: 'After reviewing evidence, we are issuing a partial refund of $25. The trailer did require some cleaning, but the full fee was excessive.', timestamp: '2026-02-11 11:00', evidence: [] }],

      resolution: {
        outcome: 'Partial refund issued',
        amount: 25,
        resolvedBy: 'Platform Admin',
        resolvedDate: '2026-02-11',
        notes: 'Evidence showed trailer required cleaning, but full fee was excessive. Partial refund issued as compromise.'
      }
    }]
    );
  }, []);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'submitted':
        return { label: 'Submitted', color: 'bg-primary/10 text-primary', icon: 'FileText' };
      case 'under_review':
        return { label: 'Under Review', color: 'bg-warning/10 text-warning', icon: 'Eye' };
      case 'awaiting_response':
        return { label: 'Awaiting Response', color: 'bg-secondary/10 text-secondary', icon: 'Clock' };
      case 'mediation':
        return { label: 'Mediation', color: 'bg-error/10 text-error', icon: 'AlertTriangle' };
      case 'resolved':
        return { label: 'Resolved', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      default:
        return { label: 'Unknown', color: 'bg-muted text-muted-foreground', icon: 'HelpCircle' };
    }
  };

  const filteredDisputes = filterStatus === 'all' ?
  disputes :
  disputes?.filter((d) => d?.status === filterStatus);

  const handleFileNewClaim = () => {
    setShowFilingWizard(true);
  };

  const handleClaimSubmit = (claimData) => {
    const newDispute = {
      id: disputes?.length + 1,
      caseNumber: `DSP-2026-${String(242 + disputes?.length)?.padStart(4, '0')}`,
      title: claimData?.title,
      disputeType: claimData?.disputeType,
      status: 'submitted',
      priority: claimData?.priority || 'medium',
      createdDate: new Date()?.toISOString()?.split('T')?.[0],
      lastUpdate: new Date()?.toISOString()?.split('T')?.[0],
      trailerName: claimData?.trailerName,
      trailerImage: claimData?.trailerImage,
      trailerImageAlt: claimData?.trailerImageAlt,
      opposingParty: claimData?.opposingParty,
      opposingPartyRole: claimData?.opposingPartyRole,
      amount: claimData?.amount,
      description: claimData?.description,
      evidence: claimData?.evidence || [],
      timeline: [
      {
        date: new Date()?.toISOString(),
        action: 'Claim submitted',
        actor: 'You',
        description: 'Initial claim filed with evidence'
      },
      {
        date: new Date()?.toISOString(),
        action: 'Notification sent',
        actor: 'System',
        description: `${claimData?.opposingPartyRole === 'owner' ? 'Owner' : 'Renter'} notified of claim`
      }],

      responses: []
    };
    setDisputes([newDispute, ...disputes]);
    setShowFilingWizard(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20 flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Dispute Resolution
                </h1>
                <p className="font-body text-muted-foreground">
                  Manage claims, submit evidence, and track resolution progress
                </p>
              </div>
              <Button
                onClick={handleFileNewClaim}
                iconName="Plus"
                size="lg"
                className="w-full sm:w-auto">
                
                File New Claim
              </Button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm text-muted-foreground">Total Claims</span>
                <Icon name="FileText" size={20} className="text-primary" strokeWidth={2} />
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">{disputes?.length}</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm text-muted-foreground">Active</span>
                <Icon name="Clock" size={20} className="text-warning" strokeWidth={2} />
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {disputes?.filter((d) => ['submitted', 'under_review', 'awaiting_response', 'mediation']?.includes(d?.status))?.length}
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm text-muted-foreground">Resolved</span>
                <Icon name="CheckCircle" size={20} className="text-success" strokeWidth={2} />
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {disputes?.filter((d) => d?.status === 'resolved')?.length}
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm text-muted-foreground">In Mediation</span>
                <Icon name="AlertTriangle" size={20} className="text-error" strokeWidth={2} />
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {disputes?.filter((d) => d?.status === 'mediation')?.length}
              </p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="bg-card rounded-lg border border-border p-2 mb-6">
            <div className="flex flex-wrap gap-2">
              {[
              { value: 'all', label: 'All Claims', icon: 'List' },
              { value: 'submitted', label: 'Submitted', icon: 'FileText' },
              { value: 'under_review', label: 'Under Review', icon: 'Eye' },
              { value: 'awaiting_response', label: 'Awaiting Response', icon: 'Clock' },
              { value: 'mediation', label: 'Mediation', icon: 'AlertTriangle' },
              { value: 'resolved', label: 'Resolved', icon: 'CheckCircle' }]?.
              map((filter) =>
              <button
                key={filter?.value}
                onClick={() => setFilterStatus(filter?.value)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md font-body font-medium transition-smooth ${
                filterStatus === filter?.value ?
                'bg-primary text-primary-foreground' :
                'text-foreground hover:bg-muted'}`
                }>
                
                  <Icon name={filter?.icon} size={18} strokeWidth={2} />
                  <span>{filter?.label}</span>
                </button>
              )}
            </div>
          </div>

          {/* Disputes List */}
          {filteredDisputes?.length === 0 ?
          <div className="bg-card rounded-lg border border-border p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} className="text-muted-foreground" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                No disputes found
              </h3>
              <p className="font-body text-muted-foreground mb-6">
                {filterStatus === 'all' ? 'You have no active or resolved disputes.' :
              `No disputes with status "${filterStatus}".`}
              </p>
              {filterStatus === 'all' &&
            <Button onClick={handleFileNewClaim} iconName="Plus">
                  File Your First Claim
                </Button>
            }
            </div> :

          <div className="space-y-4">
              {filteredDisputes?.map((dispute) =>
            <DisputeCard
              key={dispute?.id}
              dispute={dispute}
              statusConfig={getStatusConfig(dispute?.status)}
              onClick={() => setSelectedDispute(dispute)} />

            )}
            </div>
          }
        </div>
      </div>

      {/* Claim Filing Wizard Modal */}
      {showFilingWizard &&
      <ClaimFilingWizard
        onClose={() => setShowFilingWizard(false)}
        onSubmit={handleClaimSubmit} />

      }

      {/* Dispute Detail Modal */}
      {selectedDispute &&
      <DisputeDetailModal
        dispute={selectedDispute}
        statusConfig={getStatusConfig(selectedDispute?.status)}
        onClose={() => setSelectedDispute(null)}
        onUpdate={(updatedDispute) => {
          setDisputes(disputes?.map((d) => d?.id === updatedDispute?.id ? updatedDispute : d));
          setSelectedDispute(updatedDispute);
        }} />

      }
    </div>);

};

export default DisputeResolution;